import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkdownModule, MermaidAPI } from 'ngx-markdown';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ChatMessage } from 'src/app/models/conversations.interface';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CitationLinkComponent } from './citation-link/citation-link.component';
import { CitationGroupComponent } from './citation-group/citation-group.component';
import { ContentSegment, MessageRendererService, ParsedContent } from 'src/app/services/message-renderer.service';
import { SourceDocument } from 'src/app/models/advisor.interface';

@Component({
  selector: 'app-message-list',
  templateUrl: './message-list.component.html',
  styleUrls: ['./message-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MarkdownModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    CitationLinkComponent,
    CitationGroupComponent
  ]
})
export class MessageListComponent implements OnChanges {
  @Input() messages: ChatMessage[] = [];
  @Input() loading = false;
  @Input() isTyping = false;
  @Input() currentTypingMessage: ChatMessage | null = null;
  @Input() sourceDocuments: SourceDocument[] = [];

  @Output() draftSectionEvent = new EventEmitter<string>();
  @Output() snackbarEvent = new EventEmitter<string>();
  @Output() citationClicked = new EventEmitter<number>();

  // Cache for parsed content to prevent re-parsing on every change detection
  private parsedContentCache = new Map<string, ParsedContent>();
  private readonly MAX_CACHE_SIZE = 50;

  // Track the latest message timestamp to identify new messages
  private lastMessageTimestamp = 0;
  newMessageTimestamp = 0;

  mermaidChartingConfig: MermaidAPI.Config = {
    fontFamily: '"Slalom Sans", "Roboto", sans-serif',
    logLevel: MermaidAPI.LogLevel.Error,
    theme: MermaidAPI.Theme.Forest
  };

  constructor(private messageRenderer: MessageRendererService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Clear cache when sourceDocuments change to re-parse with new citation info
    if (changes['sourceDocuments']) {
      this.parsedContentCache.clear();
    }

    // Track new messages for animation
    if (changes['messages'] && this.messages.length > 0) {
      const latestMessage = this.messages[this.messages.length - 1];
      if (latestMessage.timestamp) {
        const latestTimestamp =
          typeof latestMessage.timestamp === 'number' ? latestMessage.timestamp : latestMessage.timestamp.getTime();

        if (latestTimestamp > this.lastMessageTimestamp) {
          this.newMessageTimestamp = latestTimestamp;
          this.lastMessageTimestamp = latestTimestamp;
        }
      }
    }
  }

  // Check if a message is the newest one (for animation)
  isNewMessage(message: ChatMessage): boolean {
    if (!message.timestamp) return false;
    const messageTimestamp = typeof message.timestamp === 'number' ? message.timestamp : message.timestamp.getTime();
    return messageTimestamp === this.newMessageTimestamp && message.role === 'assistant';
  }

  copyToClipboard(message: string) {
    navigator.clipboard.writeText(message);
    this.snackbarEvent.emit('Copied to clipboard');
  }

  draftSection(msg: string) {
    this.draftSectionEvent.emit(msg);
  }

  getParsedContent(messageText: string): ParsedContent {
    // Check cache first
    if (this.parsedContentCache.has(messageText)) {
      return this.parsedContentCache.get(messageText)!;
    }

    // Limit cache size
    if (this.parsedContentCache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.parsedContentCache.keys().next().value;
      if (firstKey !== undefined) {
        this.parsedContentCache.delete(firstKey);
      }
    }

    // Parse and cache
    const parsed = this.messageRenderer.transformMessage(messageText, this.sourceDocuments);
    this.parsedContentCache.set(messageText, parsed);
    return parsed;
  }

  onCitationClick(citationNumber: number): void {
    this.citationClicked.emit(citationNumber);
  }

  /**
   * Handle clicks on blocks with citations (lists, headings, etc.)
   * Handles citation clicks, expand/collapse for groups
   */
  onBlockClick(event: MouseEvent, citationNumbers: number[]): void {
    const target = event.target as HTMLElement;
    console.log('Block clicked:', target, 'classList:', target.classList, 'citationNumbers:', citationNumbers);

    // Handle expand button click
    if (target.classList.contains('citation-expand-btn')) {
      const wrapper = target.closest('.citation-group-wrapper');
      const hiddenSpan = wrapper?.querySelector('.citation-group-hidden') as HTMLElement;

      if (hiddenSpan) {
        hiddenSpan.style.display = 'inline';
        target.style.display = 'none';
      }
      event.stopPropagation();
      return;
    }

    // Handle collapse button click
    if (target.classList.contains('citation-collapse-btn')) {
      const wrapper = target.closest('.citation-group-wrapper');
      const hiddenSpan = wrapper?.querySelector('.citation-group-hidden') as HTMLElement;
      const expandBtn = wrapper?.querySelector('.citation-expand-btn') as HTMLElement;

      if (hiddenSpan && expandBtn) {
        hiddenSpan.style.display = 'none';
        expandBtn.style.display = 'inline';
      }
      event.stopPropagation();
      return;
    }

    // Handle citation click
    if (target.classList.contains('citation-text') || target.classList.contains('citation-unavailable')) {
      // Extract citation number from text content (e.g., "1", "2", etc.)
      const text = (target.textContent || '').trim();
      console.log('Citation clicked - text:', text, 'raw textContent:', target.textContent);
      const match = text.match(/^(\d+)$/);
      console.log('Match result:', match);

      if (match) {
        const num = parseInt(match[1], 10);
        console.log('Parsed number:', num, 'citationNumbers includes?', citationNumbers.includes(num));
        if (citationNumbers.includes(num)) {
          console.log('Emitting citation click:', num);
          this.onCitationClick(num);
          event.stopPropagation();
        }
      }
    }
  }

  /**
   * TrackBy function for segments to prevent unnecessary DOM recreation
   */
  trackBySegment(index: number, segment: ContentSegment): string {
    if (segment.type === 'text') {
      return `text-${index}-${segment.content.substring(0, 50)}`;
    } else if (segment.type === 'citation') {
      return `citation-${index}-${segment.citation.number}`;
    } else if (segment.type === 'citationGroup') {
      return `group-${index}-${segment.citations.map(c => c.number).join('-')}`;
    } else if (segment.type === 'blockWithCitations') {
      return `block-${index}-${segment.citationNumbers.join('-')}`;
    }
    return `segment-${index}`;
  }
}
